/**
 * SiemEventDetailsModal
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import type { SiemEvent } from '@/hooks';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Card, CardTitle } from '@/components/ui';
import { useAuth } from '@/providers/AuthProvider';
import { SiemInvestigationBar } from '@/features/siem/components/investigate';
import {
  extractSmswiserKillContextFromSiem,
  hasSmswiserKillInvestigationTarget,
} from '@/features/siem/utils/extract-smswiser-kill-context';
import { useOptionalDropTimelineLauncher } from '@/features/siem/components/drop-timeline';
import {
  extractDropTimelineContextFromSiem,
  hasDropTimelineFollowTarget,
  isSiemDropRelatedAction,
} from '@/features/siem/utils/drop-timeline';
import { SiemEventDetailsBody } from './SiemEventDetailsBody';

export const SiemEventDetailsModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: SiemEvent | null;
  onOpenAuditLog?: (auditLogId: string) => void;
  /** Stack above nested UI (e.g. drop timeline modal at z-[60]). */
  overlayZClass?: string;
}> = ({ open, onOpenChange, event, onOpenAuditLog, overlayZClass = 'z-50' }) => {
  const dropTimelineLauncher = useOptionalDropTimelineLauncher();
  const { hasPermission } = useAuth();
  const handleClose = () => onOpenChange(false);

  const title = useMemo(() => {
    if (!event) return 'SIEM Event';
    return `${event.level.toUpperCase()} — ${event.category}`;
  }, [event]);

  const auditLogId = useMemo(() => {
    const d = event?.details;
    if (!d) return '';
    const v = (d as Record<string, unknown>).auditLogId;
    return typeof v === 'string' ? v : '';
  }, [event]);

  const killContext = useMemo(
    () =>
      extractSmswiserKillContextFromSiem(
        (event?.details as Record<string, unknown> | null) ?? null,
        event?.action ?? null,
      ),
    [event],
  );
  const showKillInvestigation = Boolean(
    event && hasSmswiserKillInvestigationTarget(killContext),
  );

  const dropTimelineCtx = useMemo(
    () =>
      extractDropTimelineContextFromSiem(
        (event?.details as Record<string, unknown> | null) ?? null,
        event?.action ?? null,
      ),
    [event],
  );

  const showFollowDrop =
    Boolean(dropTimelineLauncher && event) &&
    isSiemDropRelatedAction(event?.action ?? null) &&
    hasDropTimelineFollowTarget(dropTimelineCtx) &&
    hasPermission('investigation.drop_timeline');

  const followDropLabel =
    (dropTimelineCtx.pickTargets?.length ?? 0) > 1 ? 'Follow drops' : 'Follow drop';

  const goFollowDrop = () => {
    if (!dropTimelineLauncher) return;
    dropTimelineLauncher.openDropTimeline({ ...dropTimelineCtx });
    handleClose();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={cn('fixed inset-0', overlayZClass)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/50" onClick={handleClose} aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 flex items-center justify-center p-3 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-label="SIEM event details"
          >
            <div className="w-full max-w-5xl max-h-[92vh]">
              <Card className="shadow-xl overflow-hidden">
                <div className="flex flex-col max-h-[92vh]">
                  <div className="sticky top-0 z-10 border-b bg-card">
                    <div className="flex items-start justify-between gap-3 px-6 py-4">
                      <div className="min-w-0">
                        <CardTitle>{title}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">{event?.message || '—'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {showFollowDrop ? (
                          <Button variant="outline" size="sm" onClick={goFollowDrop}>
                            {followDropLabel}
                          </Button>
                        ) : null}
                        {auditLogId && onOpenAuditLog ? (
                          <Button variant="outline" size="sm" onClick={() => onOpenAuditLog(auditLogId)}>
                            Open audit record
                          </Button>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={handleClose} aria-label="Close">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-y-auto px-6 py-5 space-y-4">
                    {!event ? (
                      <p className="text-sm text-muted-foreground">No event loaded.</p>
                    ) : (
                      <>
                        {showKillInvestigation ? (
                          <SiemInvestigationBar
                            killContext={killContext}
                            userEmail={event.userEmail}
                            canOpenWorkspace={hasPermission('drops.view')}
                            canOpenUsers={hasPermission('users.view')}
                            canOpenAuditLogs={hasPermission('audit.view')}
                          />
                        ) : null}
                        <SiemEventDetailsBody event={event} />
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default SiemEventDetailsModal;

